import { ForumPage } from "components/ForumPage"
import { useRouter } from "components/Router"
import { useTranslator } from "components/Translator"
import { Text } from "components/Text"
import { Separator } from "components/Separator"
import { Button } from "components/Button"
import { Pagination } from "components/Pagination"
import { Card } from "components/Card"
import { UserAvatar } from "domains/user/UserAvatar"
import { UserName } from "domains/user/UserName"
import { UserBadges } from "domains/user/UserBadges"
import { UserNbReviews } from "domains/user/UserNbReviews"
import { UserSuper } from "domains/user/UserSuper"
import { UserPrimaryAddress } from "domains/user/UserPrimaryAddress"
import * as styles from "./styles.css?module"
import cx from "classnames"
import { Pen, TrashAlt } from "components/Icon"
import { useConnectedUser } from "components/ConnectedUserProvider"
import { ConfirmButton } from "components/ConfirmButton"
import { MenuButton } from "components/Menu"
import { PaperPlane } from "components/Icon"
import { UserNbForumPosts } from "domains/user/UserNbForumPost"
import { useAsync } from "@react-hook/async"
import { client } from "api/client"
import { Alerter } from "components/Alert"
import { Spinner } from "components/Spinner"
import { ForumPostFollowButton } from "domains/forum-post/ForumPostFollowButton"
import { WysiwygEditor } from "components/WysiwygEditor"
import { serialize } from "remark-slate"
import { useRef } from "react"
import { Markdown } from "components/Markdown"

export function ClubForumPostPage(props) {
  return (
    <ForumPage
      metrics={props.metrics}
      post={props.post}
      title={props.post.title}
      main={
        <Main
          post={props.post}
          replies={props.replies}
          pagination={props.pagination}
          formReply={props.formReply}
        />
      }
      sidebar={<Sidebar post={props.post} initialFollowing={props.following} />}
    />
  )
}

const Main = (props) => {
  const translator = useTranslator()
  const router = useRouter()
  const connectedUser = useConnectedUser()

  return (
    <div className={"grid gap-8"}>
      <div>
        <Text variant={"headline2"} tag={"p"}>
          {props.post.title}
        </Text>
        <Text variant={"caption"} tag={"p"}>
          {translator.trans(
            "club.forum.post.nbVisits",
            { nb: props.post.nbVisits },
            "pages",
          )}
        </Text>
      </div>
      <div className={"grid gap-4"}>
        <Post post={props.post} />
        <Separator />
        <Pagination {...props.pagination} pageRange={2} />
        {props.replies.map((reply) => (
          <Post key={reply.id} post={reply} />
        ))}
        <Pagination {...props.pagination} pageRange={2} />
      </div>
      <div
        className={"grid gap-2"}
        id={"reply-form"}
        style={{ scrollMarginBlockStart: "200px" }}
      >
        <div>
          <Text variant={"headline3"} tag={"h2"}>
            {translator.trans("club.forum.post.reply.title", null, "pages")}
          </Text>
        </div>
        <div>
          {props.formReply ? (
            <ReplyForm form={props.formReply} />
          ) : props.post.locked ? (
            <div>
              <Text variant={"body2"} tag={"p"}>
                {translator.trans(
                  "club.forum.post.reply.locked",
                  null,
                  "pages",
                )}
              </Text>
            </div>
          ) : (
            <div className={"grid gap-2"}>
              <Text variant={"body2"} tag={"p"}>
                {translator.trans(
                  "club.forum.post.reply.notAllowed.text",
                  null,
                  "pages",
                )}
              </Text>
              <div>
                {connectedUser ? (
                  <Button href={router.generate("app_club_shop_plan_index")}>
                    {translator.trans(
                      "club.forum.post.reply.notAllowed.cta.connected",
                    )}
                  </Button>
                ) : (
                  <Button
                    href={router.generate("app_user_security_login", {
                      target_path: router.generate("app_club_forum_post", {
                        forumTagSlug: props.post.tag.slug,
                        forumPostSlug: props.post.slug,
                      }),
                    })}
                  >
                    {translator.trans(
                      "club.forum.post.reply.notAllowed.cta.anonymous",
                    )}
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

const Post = (props) => {
  const translator = useTranslator()
  const dateTimeFormatter = new Intl.DateTimeFormat(translator.locale, {
    dateStyle: "short",
    timeStyle: "short",
  })

  return (
    <Card>
      <div className={"flex flex-col lg:flex-row"}>
        <div
          className={
            "px-2 py-4 bg-primary-lighter lg:shrink-0 lg:grow-0 lg:w-72"
          }
        >
          <UserInfos user={props.post.user} variant={"compact"} />
        </div>
        <div
          className={
            "bg-white md:shrink md:grow md:basis-full px-4 py-2 grid gap-2"
          }
        >
          <PostActions post={props.post} />
          {props.post.title ? (
            <Text variant={"headline4"} tag={"p"}>
              {props.post.title}
            </Text>
          ) : null}
          <PostContent content={props.post.content} />
          <Text
            variant={"caption"}
            tag={"time"}
            dateTime={props.post.createdAt}
            className={"text-right"}
          >
            {dateTimeFormatter.format(new Date(props.post.createdAt))}
          </Text>
        </div>
      </div>
    </Card>
  )
}

const UserInfos = (props) => {
  const { user } = props
  const router = useRouter()

  return (
    <div className={"flex items-center space-x-4 relative"}>
      <div className={"flex flex-col items-center space-y-1 w-24 lg:w-32"}>
        <UserAvatar user={user} size={"medium"} />
        <Text
          tag={"a"}
          href={router.generate("app_account_profile_show", {
            userId: user.id,
          })}
          variant={"headline6"}
          className={cx("text-center text-dark", styles.UserInfos__name)}
        >
          <UserName user={user} variant={"compact"} />
        </Text>
        <div className={"space-x-1"}>
          <UserBadges user={user} size={"small"} />
        </div>
      </div>
      <div>
        <div className={"space-y-1"}>
          <UserPrimaryAddress user={user} />
          <UserNbReviews user={user} />
          <UserSuper user={user} />
          <UserNbForumPosts user={user} />
        </div>
      </div>
    </div>
  )
}

const PostActions = (props) => {
  const translator = useTranslator()
  const router = useRouter()
  const connectedUser = useConnectedUser()

  const deletePost = () => {
    router.redirect({
      name: "app_user_forum_delete",
      params: {
        forumPostId: props.post.id,
      },
    })
  }

  const isPostOwner = connectedUser?.id === props.post.user.id
  const isClub = connectedUser?.roles.includes("ROLE_CLUB")
  const isAdmin = connectedUser?.roles.includes("ROLE_ADMIN")

  const canEdit = (isPostOwner && isClub) || isAdmin
  const canDelete = isAdmin

  if ([canEdit, canDelete].every((condition) => !condition)) {
    return null
  }

  return (
    <div className={"flex gap-2 justify-end"}>
      {canEdit ? (
        <Button
          href={router.generate("app_user_forum_edit", {
            forumPostId: props.post.id,
          })}
          iconLeft={<Pen className={"w-4 h-4"} />}
          size={"small"}
          variant={"outline"}
          color={"secondary"}
        >
          {translator.trans("club.forum.post.post.actions.edit", null, "pages")}
        </Button>
      ) : null}
      {canDelete ? (
        <ConfirmButton
          variant="outline"
          size={"small"}
          color="danger"
          iconLeft={<TrashAlt className="w-4" />}
          confirmTitle={translator.trans(
            "club.forum.post.post.actions.delete.confirmTitle",
            null,
            "pages",
          )}
          confirmContent={translator.trans(
            "club.forum.post.post.actions.delete.confirmContent",
            null,
            "pages",
          )}
          confirmLabel={translator.trans(
            "club.forum.post.post.actions.delete.confirmLabel",
            null,
            "pages",
          )}
          onConfirm={deletePost}
        >
          {translator.trans(
            "club.forum.post.post.actions.delete.cta",
            null,
            "pages",
          )}
        </ConfirmButton>
      ) : null}
    </div>
  )
}

const ReplyForm = (props) => {
  const translator = useTranslator()
  const router = useRouter()

  const [{ status }, addMessage] = useAsync(async (form) => {
    const data = new FormData(form)

    data.append(
      `${props.form.title}[_token]`,
      props.form.properties._token.default,
    )

    const contentKey = `${props.form.title}[content]`

    if (!data.has(contentKey)) {
      data.append(
        `${props.form.title}[content]`,
        messageContent.current
          .map((v) => serialize(v))
          .join("")
          .trim(),
      )
    }

    try {
      const response = await client.request({
        method: form.method,
        url: form.action,
        data,
      })

      if (response.data.message) {
        Alerter.success(response.data.message)
      }

      if (response.data.redirect_url) {
        if (response.data.message) {
          setTimeout(() => {
            router.redirect(response.data.redirect_url)
          }, 2000)
        } else {
          router.redirect(response.data.redirect_url)
        }
      }
    } catch (err) {
      console.error(err)

      if (err.response.data.message) {
        Alerter.error(err.response.data.message)
      } else if (err.response.status === 403) {
        Alerter.error(
          translator.trans("Discussion.error.403", null, "components"),
        )
      } else {
        Alerter.error(
          translator.trans("Disccussion.error.unknown", null, "component"),
        )
        console.error("Error while sending message: ", err)
      }
    }
  })

  const handleSubmit = async (e) => {
    e.preventDefault()

    addMessage(e.currentTarget)
  }

  const messageContent = useRef(null)

  return (
    <form
      onSubmit={handleSubmit}
      method={props.form.options.form.method}
      action={props.form.options.form.action}
    >
      <div className={"flex gap-4 items-end"}>
        <div className={"grow basis-full"}>
          <WysiwygEditor
            placeholder={
              props.form.properties.content.options.attr?.placeholder
            }
            onChange={(value) => (messageContent.current = value)}
            enabledWidgets={
              props.form.properties.content.options.wysiwyg.options
            }
          />
        </div>
        <div className={"shrink-0 pb-2"}>
          <MenuButton
            type="submit"
            color="default"
            illustration={
              status === "loading" ? (
                <Spinner size={20} />
              ) : (
                <PaperPlane className="w-3.5 text-white" />
              )
            }
            disabled={status === "loading"}
          />
        </div>
      </div>
    </form>
  )
}

const Sidebar = (props) => {
  const connectedUser = useConnectedUser()

  if (!connectedUser) {
    return null
  }

  return (
    <ForumPostFollowButton
      postId={props.post.id}
      initialFollowing={props.initialFollowing}
    />
  )
}

const PostContent = (props) => {
  return <Markdown linkTarget={"_blank"}>{props.content}</Markdown>
}
